/**
 * 使い方ガイド（/guide・2026-10-07 console/guide-page）。置き場所と「できること」の表の WHY は guidePage.ts。
 * 1 画面＋少しのスクロールに収める: 始め方 3 段 → できること（各 1〜2 行＋コピーできる例文）→ 料金の 1 行と困ったとき。
 * 例文の並び・見た目はホームの頼み方の例（components/AskExamples.tsx の .home-examples）と同じ部品の形にそろえる
 *   （新しい見た目を増やさない）。例文は {product} を t() が差し込んだ後の文をそのままコピーする。
 * 接続の有無で出し分けない（つないだ後に「何を頼めるか」を見に来る人のための画面でもある）。
 */
import { Link } from 'react-router-dom';
import { ConnectPictures } from '../components/ConnectPictures';
import { PublishFlow } from '../components/PublishFlow';
import { CopyButton, PageHeader } from '../components/ui';
import { mcpEndpoint } from '../homeFlow';
import { useResource } from '../hooks';
import { ConnectBody } from './HomeParts';
import { GUIDE_ITEMS, SUPPORT_EMAIL } from '../guidePage';
import { useT, type MessageKey } from '../i18n';
import { PREVIEW_NAME } from '../productName';
import { TopBar } from './ProjectsPage';

export function GuidePage() {
  const { t, tr } = useT();
  const startAsk = t('guide.start.ask.example');
  const platform = useResource<{ mcpUrl?: unknown; previewMcpUrl?: unknown; accountSignIn?: unknown }>('/v1/platform/info');
  // KUMODeck でログインはサーバーで使えるときだけ案内する（使えないものを書かない・2026-10-07 core/kumodeck-signin）
  const accountSignIn = platform.data?.accountSignIn === true;
  // Preview（検証用の MCP）の URL。サーバーが返すとき（開いた形のときだけ・platform/routes.ts の previewMcpUrl）だけ段を出す = 直書きしない（mcpUrl と同じ形）
  const previewMcp = mcpEndpoint(platform.data?.previewMcpUrl);
  return (
    <div className="page-wrap">
      <TopBar />
      <main className="container narrow guide" id="main">
        <PageHeader title={t('guide.title')} lead={t('guide.lead')} />

        <section className="card" aria-labelledby="guide-start">
          <h2 id="guide-start" className="home-section-title">
            {t('guide.start.title')}
          </h2>
          <ol className="guide-steps">
            <li>
              <h3>{t('guide.start.connect.title')}</h3>
              <p>{t('guide.start.connect.body')}</p>
              {/* 2026-10-07 founder「これは使用法に入れればよいよね」: 別の画面（/connect）へ飛ばさず、コピペの 1 行をここに出す（ホームの段と同じ ConnectBody）。
                  2026-10-16 console/guide-preview（founder の決定）: Preview が開いているときは、貼る文を Preview の 1 つだけにする。
                  console/home-preview（同日）からは ConnectBody 自身が previewMcpUrl を読む（ホームの段と同じ決まり・ここから渡さない） */}
              <ConnectBody mcpUrl={mcpEndpoint(platform.data?.mcpUrl)} />
              {/* AI ごとの 4 段の絵（ホームの「つなぐ」の段と同じ部品・2026-10-19 design/visual-guides） */}
              <ConnectPictures />
            </li>
            <li>
              <h3>{t('guide.start.ask.title')}</h3>
              <p>{t('guide.start.ask.body')}</p>
              <ul className="home-examples">
                <AskLine ask={startAsk} />
              </ul>
            </li>
            <li>
              <h3>{t('guide.start.publish.title')}</h3>
              <p>{t('guide.start.publish.body')}</p>
              {/* テスト版 →「公開して」→ 1 回だけ確認 → みんなが開ける、の絵（2026-10-19 design/visual-guides） */}
              <PublishFlow />
            </li>
          </ol>
        </section>

        <section className="card" aria-labelledby="guide-can">
          <h2 id="guide-can" className="home-section-title">
            {t('guide.can.title')}
          </h2>
          <p className="muted">{t('guide.can.lead')}</p>
          <ul className="guide-items">
            {GUIDE_ITEMS.map((item) => {
              // キーは guidePage.test.ts が en / ja に全部あることを確かめている
              const k = (part: string) => `guide.item.${item.id}.${part}` as MessageKey;
              const ask = t(k('ask'));
              return (
                <li key={item.id}>
                  <h3>{t(k('title'))}</h3>
                  <p>{t(item.id === 'signIn' && accountSignIn ? 'guide.item.signIn.bodyAccount' : k('body'))}</p>
                  <div className="home-examples guide-ask">
                    <q>{ask}</q>
                    <CopyButton text={ask} />
                  </div>
                </li>
              );
            })}
          </ul>
        </section>

        {/* 2026-10-16 console/guide-preview: Preview の段は「頼めること」の下（指示書）。説明はここ 1 か所（DM はこのページへのリンクだけ）。
            貼る文の見た目は「AIをつなぐ」の貼る文（HomeParts ConnectBody）と同じ部品（.home-command.home-ask-block の pre＋CopyButton） */}
        {previewMcp && <PreviewSection />}

        <section className="card" aria-labelledby="guide-cost">
          <h2 id="guide-cost" className="home-section-title">
            {t('guide.cost.title')}
          </h2>
          <p>{t('guide.cost.body')}</p>
          <p className="guide-links">
            {/* 行き先はトップバーの残高（BalanceChip）とお金のナビの「使った量」と同じ */}
            <Link to="/money/prepaid">{t('guide.cost.balanceLink')}</Link>
            <Link to="/money/usage">{t('guide.cost.usageLink')}</Link>
          </p>
          <p className="muted small">
            {tr('guide.help', { email: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> })}
          </p>
        </section>
      </main>
    </div>
  );
}

/** 始め方の段の例文 1 行（できることの例文と同じ形） */
function AskLine({ ask }: { ask: string }) {
  return (
    <li>
      <q>{ask}</q>
      <CopyButton text={ask} />
    </li>
  );
}

/**
 * Preview の段（2026-10-16 console/guide-preview・文言の WHY は en.guide.ts の guide.preview.*）。
 * 見出しは「PREVIEW · KUMODeck Preview (v0.2)」の 1 行（名前だけ大文字にしない）。中は 3D と Blender の項目・ほかに使えるもの・費用（貼る文は「AIをつなぐ」）
 */
function PreviewSection() {
  const { t, tr } = useT();
  // 見出しはほかの段と同じ形（.home-section-title = 大文字）。名前は製品名なので大文字にしない（KUMODECK PREVIEW (V0.2) にしない）
  const name = <span className="guide-preview-name">{PREVIEW_NAME}</span>;
  // 2026-10-16（founder の決定・管理）: 貼る文はここに置かない = 「AIをつなぐ」の 1 つだけ（Preview が開いているときはそれが Preview の文）。
  //   ここは「何ができるか・いくらか」の説明だけ
  return (
    <section className="card guide-preview" aria-labelledby="guide-preview">
      <h2 id="guide-preview" className="home-section-title">
        {tr('guide.preview.title', { preview: name })}
      </h2>
      <p className="muted">{t('guide.preview.lead')}</p>
      <div className="guide-items">
        <div>
          <h3>{t('guide.preview.model3d.title')}</h3>
          <p>{t('guide.preview.model3d.body')}</p>
          <p>{t('guide.preview.more')}</p>
          <p>{t('guide.preview.cost')}</p>
        </div>
      </div>
    </section>
  );
}
